import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Avatar,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import { AdminPanelSettings, Email, VerifiedUser } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../services/api';
import logo from '../../Transparent.png';

// Dev-only shortcut: the OTP flow needs a real mailbox and an allow-listed
// admin account, neither of which exists against a local backend. Requires
// REACT_APP_ENABLE_DEV_LOGIN in .env.local (gitignored) AND a non-production
// build, so it can never reach a deployed portal.
const DEV_LOGIN_ENABLED =
  process.env.NODE_ENV !== 'production' &&
  process.env.REACT_APP_ENABLE_DEV_LOGIN === 'true';

const AdminLogin = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { setUser, isAuthenticated, user } = useAuth();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  // Whatever the backend actually did — a real email in production, or "check
  // the server console" when SMTP is not configured locally.
  const [otpMessage, setOtpMessage] = useState('');
  const [countdown, setCountdown] = useState(0);

  // Auto-redirect if already authenticated as admin
  useEffect(() => {
    if (isAuthenticated && user?.userType === 'admin') {
      navigate('/dashboard');
    }
  }, [isAuthenticated, user, navigate]);

  // Countdown timer for resend OTP
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/admin/request-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send OTP');
      }

      setOtpSent(true);
      setOtpMessage(data.message || '');
      setCountdown(60); // 60 seconds countdown
      setError('');

    } catch (error) {
      setError(error.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  // Local development only — see DEV_LOGIN_ENABLED above. Hits the backend's
  // dev-login route (itself gated behind ENABLE_DEV_LOGIN) so the token is
  // signed by the same backend the rest of the app talks to.
  const handleDevLogin = async () => {
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/dev-login/admin`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Dev login failed');
      }

      setUser({ ...data.user, token: data.token });
      navigate('/dashboard');
    } catch (error) {
      setError(error.message || 'Dev login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/admin/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }, 
        body: JSON.stringify({ email, otp }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Invalid OTP');
      }

      // Store token and user data - pass token within user object for AuthContext
      setUser({ ...data.user, token: data.token });
      
      navigate('/dashboard');
    } catch (error) {
      setError(error.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: '16px',
            border: '1px solid',
            borderColor: 'divider',
            width: '100%',
            background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0.02) 100%)',
          }}
        >
          <Box
            sx={{
              m: 1,
              width: 80,
              height: 80,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <img 
              src={logo} 
              alt="EduMetrix Logo" 
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          </Box>
          
          <Typography 
            component="h1" 
            variant={isMobile ? 'h5' : 'h4'}
            sx={{ 
              fontWeight: 700,
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 3,
            }}
          >
            EduMetrix
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          {otpSent && (
            <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
              {otpMessage || `OTP has been sent to ${email}. Please check your email.`}
            </Alert>
          )}

          {!otpSent ? (
            <Box component="form" onSubmit={handleRequestOTP} sx={{ width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Please enter your email ID"
                name="email"
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                  },
                }}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <Email />}
                sx={{
                  mt: 3,
                  mb: 2,
                  borderRadius: '12px',
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                  },
                }}
              >
                {loading ? 'Sending OTP...' : 'Send OTP'}
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleVerifyOTP} sx={{ width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="otp"
                label="Enter 6-digit OTP"
                name="otp"
                autoComplete="off"
                autoFocus
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                inputProps={{
                  maxLength: 6,
                  style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                  },
                }}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || otp.length !== 6}
                startIcon={loading ? <CircularProgress size={20} /> : <VerifiedUser />}
                sx={{
                  mt: 3,
                  mb: 2,
                  borderRadius: '12px',
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                  },
                }}
              >
                {loading ? 'Verifying...' : 'Verify & Login'}
              </Button>

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  variant="text"
                  disabled={countdown > 0 || loading}
                  onClick={() => {
                    setOtpSent(false);
                    setOtp('');
                    setError('');
                  }}
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                  }}
                >
                  {countdown > 0 ? `Resend OTP in ${countdown}s` : 'Request New OTP'}
                </Button>
              </Box>
            </Box>
          )}

          {DEV_LOGIN_ENABLED && (
            <Box sx={{ width: '100%', mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                disabled={loading}
                onClick={handleDevLogin}
                sx={{ borderRadius: '12px', textTransform: 'none' }}
              >
                Dev login (local backend)
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                Local development only — {API_BASE_URL}
              </Typography>
            </Box>
          )}

        </Paper>
      </Box>
    </Container>
  );
};

export default AdminLogin;